import type { Logger } from '@thesavingsgroup/logger/Logger';
import type { RemoteConfiguration } from './types/RemoteConfiguration';
import { generateDestinationURL } from './generateDestinationURL';
import type { RedirectConfiguration } from './types/RedirectConfiguration';
import type { ESEMode } from './types/ESEMode';
import type { GetRemoteConfiguration } from './getConfigurationFromContentful';
import { getBrandsToDisplay } from './getBrandsToDisplay';

type ToRemoteConfigurationDependencies = {
  logger: Logger;
  mode: ESEMode;
};

export const toRemoteConfiguration =
  ({
    logger,
    mode = import.meta.env.MODE as ESEMode,
  }: ToRemoteConfigurationDependencies) =>
  async (
    remote: GetRemoteConfiguration,
    redirect: RedirectConfiguration,
    uniqueIdentifier?: string,
  ): Promise<RemoteConfiguration | null> => {
    logger.info('[REMOTE_CONFIGURATION] merge configuration', {
      remote,
      redirect,
      uniqueIdentifier,
    });

    if (!remote) {
      return null;
    }

    const [config] = remote.items;

    if (!config) {
      return null;
    }

    const generateURL = generateDestinationURL(mode);

    const getCode = (entry: string) =>
      // @ts-expect-error - path is correct
      config.fields[entry]?.fields.leadChannelCode;

    return {
      brandsToDisplay: getBrandsToDisplay({
        displayAutopayBrand: config.fields.displayAutopayBrand,
        displayRateGeniusBrand: config.fields.displayRateGeniusBrand,
        displayTreslBrand: config.fields.displayTreslBrand,
      }),
      buttonCopy: config.fields.buttonCopy,
      configurationCode: config.fields.configurationCode,
      disclaimer: config.fields.disclaimer,
      displayFooterLogo: config.fields.displayFooterLogo,
      heading: config.fields.heading,
      leaseBuyout: {
        autopay: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('autopayLeaseBuyoutLeadChannel'),
          originalURL:
            redirect.leaseBuyout.autopay[
              config.fields.autopayLeaseBuyoutDestination
            ][mode],
          prodOverride: config.fields.autopayLeaseBuyoutProdUrlOverride,
          stageOverride: config.fields.autopayLeaseBuyoutStageUrlOverride,
          uniqueIdentifier,
        }),
        rateGenius: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('rateGeniusLeaseBuyoutLeadChannel'),
          originalURL:
            redirect.leaseBuyout.rateGenius[
              config.fields.rateGeniusLeaseBuyoutDestination
            ][mode],
          prodOverride: config.fields.rateGeniusLeaseBuyoutProdUrlOverride,
          stageOverride: config.fields.rateGeniusLeaseBuyoutStageUrlOverride,
          uniqueIdentifier,
        }),
        tresl: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('treslLeaseBuyoutLeadChannel'),
          originalURL:
            redirect.leaseBuyout.tresl[
              config.fields.treslLeaseBuyoutDestination
            ][mode],
          prodOverride: config.fields.treslLeaseBuyoutProdUrlOverride,
          stageOverride: config.fields.treslLeaseBuyoutStageUrlOverride,
          uniqueIdentifier,
        }),
      },
      name: config.fields.name,
      offerHeading: config.fields.offerHeading,
      // @ts-expect-error - path is correct
      primaryLogo: config.fields.primaryLogo?.fields.file.url,
      purchase: {
        autopay: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('autopayPurchaseLeadChannel'),
          originalURL:
            redirect.purchase.autopay[config.fields.autopayPurchaseDestination][
              mode
            ],
          prodOverride: config.fields.autopayPurchaseProdUrlOverride,
          stageOverride: config.fields.autopayPurchaseStageUrlOverride,
          uniqueIdentifier,
        }),
        rateGenius: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('rateGeniusPurchaseLeadChannel'),
          originalURL:
            redirect.purchase.rateGenius[
              config.fields.rateGeniusPurchaseDestination
            ][mode],
          prodOverride: config.fields.rateGeniusPurchaseProdUrlOverride,
          stageOverride: config.fields.rateGeniusPurchaseStageUrlOverride,
          uniqueIdentifier,
        }),
        tresl: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('treslPurchaseLeadChannel'),
          originalURL:
            redirect.purchase.tresl[config.fields.treslPurchaseDestination][
              mode
            ],
          prodOverride: config.fields.treslPurchaseProdUrlOverride,
          stageOverride: config.fields.treslPurchaseStageUrlOverride,
          uniqueIdentifier,
        }),
      },
      refinance: {
        autopay: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('autopayRefinanceLeadChannel'),
          originalURL:
            redirect.refinance.autopay[
              config.fields.autopayRefinanceDestination
            ][mode],
          prodOverride: config.fields.autopayRefinanceProdUrlOverride,
          stageOverride: config.fields.autopayRefinanceStageUrlOverride,
          uniqueIdentifier,
        }),
        rateGenius: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('rateGeniusRefinanceLeadChannel'),
          originalURL:
            redirect.refinance.rateGenius[
              config.fields.rateGeniusRefinanceDestination
            ][mode],
          prodOverride: config.fields.rateGeniusRefinanceProdUrlOverride,
          stageOverride: config.fields.rateGeniusRefinanceStageUrlOverride,
          uniqueIdentifier,
        }),
        tresl: generateURL({
          configurationCode: config.fields.configurationCode,
          leadChannelCode: getCode('treslRefinanceLeadChannel'),
          originalURL:
            redirect.refinance.tresl[config.fields.treslRefinanceDestination][
              mode
            ],
          prodOverride: config.fields.treslRefinanceProdUrlOverride,
          stageOverride: config.fields.treslRefinanceStageUrlOverride,
          uniqueIdentifier,
        }),
      },
      subheading: config.fields.subheading,
    };
  };
